import React, { useState, useRef, useEffect } from 'react';
import './AvatarPositionEditor.css';

/**
 * AvatarPositionEditor - Premium avatar positioning component
 * Allows users to drag, position, and rotate their photo within a circular frame
 * before uploading to ensure perfect framing
 */
const AvatarPositionEditor = ({ file, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [image, setImage] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0); // 0, 90, 180, or 270 degrees
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isProcessing, setIsProcessing] = useState(false);

  const CANVAS_SIZE = 300; // Smaller to fit without scrolling
  const PREVIEW_SIZE = 80;

  // Load image from file
  useEffect(() => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        
        // Center the image initially and scale to fit
        const canvas = canvasRef.current;
        if (canvas) {
          const scaleToFit = Math.min(
            CANVAS_SIZE / img.width,
            CANVAS_SIZE / img.height
          );
          setScale(scaleToFit);
          
          // Center the image
          const scaledWidth = img.width * scaleToFit;
          const scaledHeight = img.height * scaleToFit;
          setPosition({
            x: (CANVAS_SIZE - scaledWidth) / 2,
            y: (CANVAS_SIZE - scaledHeight) / 2
          });
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, [file]);

  // Draw image on canvas with rotation
  useEffect(() => {
    if (!image || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    
    // Draw image with rotation
    ctx.save();
    
    // Move to center for rotation
    ctx.translate(CANVAS_SIZE / 2, CANVAS_SIZE / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-CANVAS_SIZE / 2, -CANVAS_SIZE / 2);
    
    // Draw image
    ctx.drawImage(
      image,
      position.x,
      position.y,
      image.width * scale,
      image.height * scale
    );
    ctx.restore();
    
    // Draw circular mask overlay
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    
    // Draw circular border
    ctx.save();
    ctx.strokeStyle = '#4a6b25';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2 - 1, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    
    // Update preview
    updatePreview();
  }, [image, position, scale, rotation]);

  // Update preview canvas
  const updatePreview = () => {
    if (!image || !previewCanvasRef.current || !canvasRef.current) return;

    const previewCanvas = previewCanvasRef.current;
    const previewCtx = previewCanvas.getContext('2d');
    const mainCanvas = canvasRef.current;
    
    // Clear preview
    previewCtx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
    
    // Draw scaled down version
    previewCtx.drawImage(mainCanvas, 0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
  };

  // Handle mouse down
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // Handle touch start (mobile)
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      });
    }
  };

  // Handle mouse move
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  // Handle touch move (mobile)
  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle zoom
  const handleZoom = (direction) => {
    const zoomFactor = direction === 'in' ? 1.1 : 0.9;
    const newScale = Math.max(0.1, Math.min(3, scale * zoomFactor));
    
    // Adjust position to zoom towards center
    const centerX = CANVAS_SIZE / 2;
    const centerY = CANVAS_SIZE / 2;
    const scaleDiff = newScale / scale;
    
    setScale(newScale);
    setPosition({
      x: centerX - (centerX - position.x) * scaleDiff,
      y: centerY - (centerY - position.y) * scaleDiff
    });
  };

  // Handle rotation in 90-degree increments
  const handleRotate = () => {
    const newRotation = (rotation + 90) % 360;
    setRotation(newRotation);
  };

  // Handle save
  const handleSave = async () => {
    if (!canvasRef.current) return;
    
    setIsProcessing(true);
    
    try {
      // Create final canvas at 400x400
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = 400;
      finalCanvas.height = 400;
      const finalCtx = finalCanvas.getContext('2d');
      
      // Apply rotation to final image
      finalCtx.save();
      finalCtx.translate(200, 200);
      finalCtx.rotate((rotation * Math.PI) / 180);
      finalCtx.translate(-200, -200);
      
      // Draw image (scale up from preview size)
      const scaleRatio = 400 / CANVAS_SIZE;
      finalCtx.drawImage(
        image,
        position.x * scaleRatio,
        position.y * scaleRatio,
        image.width * scale * scaleRatio,
        image.height * scale * scaleRatio
      );
      finalCtx.restore();
      
      // Apply circular mask
      finalCtx.globalCompositeOperation = 'destination-in';
      finalCtx.beginPath();
      finalCtx.arc(200, 200, 200, 0, Math.PI * 2);
      finalCtx.fill();
      
      // Convert to blob
      finalCanvas.toBlob((blob) => {
        if (blob) {
          // Create a File object from the blob
          const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
          onSave(file);
        }
        setIsProcessing(false);
      }, 'image/jpeg', 0.9);
      
    } catch (error) {
      console.error('Error processing avatar:', error);
      setIsProcessing(false);
    }
  };

  return (
    <div className="camera-modal-overlay">
      <div className="camera-modal avatar-position-modal">
        <div className="camera-modal-header">
          <h3>Position Your Photo</h3>
          <button 
            className="camera-close-button"
            onClick={onCancel}
            disabled={isProcessing}
          >
            ×
          </button>
        </div>

        <div className="camera-modal-body avatar-position-body">
          {/* Main Canvas */}
          <div className="avatar-position-canvas-wrapper">
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleMouseUp}
              className="avatar-position-canvas"
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            />
          </div>

          {/* Controls */}
          <div className="avatar-position-controls">
            <button
              className="avatar-position-control-btn"
              onClick={() => handleZoom('out')}
              disabled={isProcessing || scale <= 0.1}
              title="Zoom Out"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
                <line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </button>
            
            <button
              className="avatar-position-control-btn"
              onClick={() => handleZoom('in')}
              disabled={isProcessing || scale >= 3}
              title="Zoom In"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
                <line x1="11" y1="8" x2="11" y2="14"/>
                <line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </button>

            <button
              className="avatar-position-control-btn"
              onClick={handleRotate}
              disabled={isProcessing}
              title="Rotate 90°"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
            </button>

            {/* Preview */}
            <div className="avatar-position-preview">
              <canvas
                ref={previewCanvasRef}
                width={PREVIEW_SIZE}
                height={PREVIEW_SIZE}
                className="avatar-position-preview-canvas"
              />
            </div>
          </div>
        </div>

        <div className="camera-modal-footer">
          <button
            className="camera-capture-button"
            onClick={handleSave}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Save & Upload'}
          </button>
          <button
            className="camera-cancel-button"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarPositionEditor;

